import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { templatesApi, packetsApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, 
  FileText, 
  Trash2, 
  Upload, 
  RefreshCw,
  MoreVertical,
  Eye,
  User,
  Building2,
  Package,
  Files,
  Edit2,
  Briefcase,
  Copy
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Bullhorn entity colors (from Bullhorn official brand guidelines)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', lightBg: 'bg-[#4CAF50]/10', iconColor: 'text-[#4CAF50]' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', lightBg: 'bg-[#F9A825]/10', iconColor: 'text-[#F9A825]' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', lightBg: 'bg-[#1a4a5e]/10', iconColor: 'text-[#1a4a5e]' },
};

export default function TemplatesPage() {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isPacketOpen, setIsPacketOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '', entity_type: 'candidate', bullhorn_file_type: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingPacket, setEditingPacket] = useState(null);
  const [bullhornFileTypes, setBullhornFileTypes] = useState([]);
  const [newPacket, setNewPacket] = useState({
    name: '',
    description: '',
    template_ids: [],
    entity_type: 'candidate',
    completion_message: '',
    email_subject: '',
    email_message: ''
  });
  const navigate = useNavigate();
  const { getTenantPath } = useAuth();

  useEffect(() => {
    loadData();
    // loadData has no dependencies that change after mount; safe to fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    const [templatesResult, packetsResult] = await Promise.allSettled([
      templatesApi.list(),
      packetsApi.list()
    ]);

    if (templatesResult.status === 'fulfilled') {
      setTemplates(templatesResult.value.data);
    } else {
      toast.error('Failed to load templates');
    }

    if (packetsResult.status === 'fulfilled') {
      setPackets(packetsResult.value.data);
    } else {
      toast.error('Failed to load packets');
    }

    // Try to load Bullhorn file types (silently fail if not connected)
    try {
      const fileTypesRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bullhorn/file-types`);
      const types = fileTypesRes.data.file_types || [];
      setBullhornFileTypes(types.map(t => typeof t === 'object' ? t.value : t));
    } catch (e) {
      setBullhornFileTypes([
        'CV', 'Resume', 'Application', 'Contract', 'Reference', 
        'Right to Work', 'ID Document', 'Tax Form', 'Training Certificate',
        'Background Check', 'Medical', 'Other'
      ]);
    }

    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTemplate.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('name', newTemplate.name);
      formData.append('description', newTemplate.description || '');
      formData.append('entity_type', newTemplate.entity_type);
      formData.append('bullhorn_file_type', newTemplate.bullhorn_file_type || '');
      formData.append('fields', JSON.stringify([]));
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      await templatesApi.create(formData);
      toast.success('Template created successfully');
      setIsCreateOpen(false);
      setNewTemplate({ name: '', description: '', entity_type: 'candidate', bullhorn_file_type: '' });
      setSelectedFile(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create template'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await templatesApi.delete(id);
      toast.success('Template deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (id, name) => {
    try {
      await templatesApi.duplicate(id);
      toast.success(`Duplicated "${name}"`);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to duplicate template'));
    }
  };

  // Packet handlers
  const handleCreatePacket = async (e) => {
    e.preventDefault();
    if (!newPacket.name.trim()) {
      toast.error('Please enter a packet name');
      return;
    }
    if (newPacket.template_ids.length === 0) {
      toast.error('Please select at least one document');
      return;
    }
    
    setCreating(true);
    try {
      if (editingPacket) {
        await packetsApi.update(editingPacket.id, newPacket);
        toast.success('Packet updated successfully');
      } else {
        await packetsApi.create(newPacket);
        toast.success('Packet created successfully');
      }
      setIsPacketOpen(false);
      setEditingPacket(null);
      setNewPacket({
        name: '',
        description: '',
        template_ids: [],
        entity_type: 'candidate',
        completion_message: '',
        email_subject: '',
        email_message: ''
      });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save packet'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePacket = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await packetsApi.delete(id);
      toast.success('Packet deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete packet');
    }
  };

  const handleEditPacket = (packet) => {
    setEditingPacket(packet);
    setNewPacket({
      name: packet.name,
      description: packet.description || '',
      template_ids: packet.template_ids || [],
      entity_type: packet.entity_type || 'candidate',
      completion_message: packet.completion_message || '',
      email_subject: packet.email_subject || '',
      email_message: packet.email_message || ''
    });
    setIsPacketOpen(true);
  };

  const toggleTemplateInPacket = (templateId) => {
    setNewPacket(prev => ({
      ...prev,
      template_ids: prev.template_ids.includes(templateId)
        ? prev.template_ids.filter(id => id !== templateId)
        : [...prev.template_ids, templateId]
    }));
  };

  // Filter templates by entity type for packet creation.
  // Placement packets are special — they can bundle BOTH candidate-mapped
  // and contact-mapped templates so the system can fan-out documents to
  // the right party at send time.
  const filteredTemplates = newPacket.entity_type === 'placement'
    ? templates.filter((t) => ['candidate', 'contact', 'placement'].includes(t.entity_type || 'candidate'))
    : templates.filter((t) => (t.entity_type || 'candidate') === newPacket.entity_type);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="templates-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Templates & Packets</h1>
            <p className="text-muted-foreground font-body mt-1">
              Manage documents and group them into packets
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex items-center justify-between">
            <TabsList className="grid grid-cols-2 w-fit">
              <TabsTrigger value="templates" className="gap-2" data-testid="templates-tab">
                <FileText className="w-4 h-4" />
                Templates ({templates.length})
              </TabsTrigger>
              <TabsTrigger value="packets" className="gap-2" data-testid="packets-tab">
                <Package className="w-4 h-4" />
                Packets ({packets.length})
              </TabsTrigger>
            </TabsList>

            {/* Context-aware Create Button */}
            {activeTab === 'templates' ? (
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-template-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Template</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4" data-testid="create-template-form">
                    <div className="space-y-2">
                      <Label htmlFor="name">Template Name</Label>
                      <Input
                        id="name"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        placeholder="e.g., Employment Contract"
                        className="input-focus"
                        data-testid="template-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description (optional)</Label>
                      <Textarea
                        id="description"
                        value={newTemplate.description}
                        onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                        placeholder="Brief description of this template"
                        className="input-focus"
                        rows={3}
                        data-testid="template-description-input"
                      />
                    </div>
                    
                    {/* Entity Type Selector */}
                    <div className="space-y-2">
                      <Label>Template Type</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setNewTemplate({ ...newTemplate, entity_type: 'candidate' })}
                          className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                            newTemplate.entity_type === 'candidate'
                              ? 'border-[#4CAF50] bg-[#4CAF50]/10'
                              : 'border-border hover:border-[#4CAF50]/50'
                          }`}
                          data-testid="entity-type-candidate"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            newTemplate.entity_type === 'candidate' ? 'bg-[#4CAF50]' : 'bg-[#4CAF50]/20'
                          }`}>
                            <User className={`w-5 h-5 ${newTemplate.entity_type === 'candidate' ? 'text-white' : 'text-[#4CAF50]'}`} />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">Candidate</p>
                            <p className="text-xs text-muted-foreground">For job applicants</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewTemplate({ ...newTemplate, entity_type: 'contact' })}
                          className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                            newTemplate.entity_type === 'contact'
                              ? 'border-[#F9A825] bg-[#F9A825]/10'
                              : 'border-border hover:border-[#F9A825]/50'
                          }`}
                          data-testid="entity-type-contact"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            newTemplate.entity_type === 'contact' ? 'bg-[#F9A825]' : 'bg-[#F9A825]/20'
                          }`}>
                            <Building2 className={`w-5 h-5 ${newTemplate.entity_type === 'contact' ? 'text-white' : 'text-[#F9A825]'}`} />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">Contact</p>
                            <p className="text-xs text-muted-foreground">For client contacts</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewTemplate({ ...newTemplate, entity_type: 'placement' })}
                          className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                            newTemplate.entity_type === 'placement'
                              ? 'border-[#1a4a5e] bg-[#1a4a5e]/10'
                              : 'border-border hover:border-[#1a4a5e]/50'
                          }`}
                          data-testid="entity-type-placement"
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            newTemplate.entity_type === 'placement' ? 'bg-[#1a4a5e]' : 'bg-[#1a4a5e]/20'
                          }`}>
                            <Briefcase className={`w-5 h-5 ${newTemplate.entity_type === 'placement' ? 'text-white' : 'text-[#1a4a5e]'}`} />
                          </div>
                          <div className="text-left">
                            <p className="font-medium">Placement</p>
                            <p className="text-xs text-muted-foreground">For active placements</p>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Bullhorn File Type */}
                    <div className="space-y-2">
                      <Label>Bullhorn File Type</Label>
                      <select
                        value={newTemplate.bullhorn_file_type}
                        onChange={(e) => setNewTemplate({ ...newTemplate, bullhorn_file_type: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="bullhorn-file-type-select"
                      >
                        <option value="">Select file type...</option>
                        {bullhornFileTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">
                        This determines where the document is saved in Bullhorn
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Document File (PDF)</Label>
                      <div 
                        className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors duration-200"
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                          const f = e.dataTransfer.files[0];
                          if (f && f.name.toLowerCase().endsWith('.pdf')) {
                            setSelectedFile(f);
                          } else {
                            toast.error('Please drop a PDF file');
                          }
                        }}
                      >
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setSelectedFile(e.target.files[0])}
                          className="hidden"
                          id="file-upload"
                          data-testid="template-file-input"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          {selectedFile ? (
                            <div className="flex items-center justify-center gap-2 text-primary">
                              <FileText className="w-5 h-5" />
                              <span className="font-medium">{selectedFile.name}</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">
                                Click to upload or drag and drop
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                PDF only (max 10MB)
                              </p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setIsCreateOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={creating}
                        data-testid="submit-template-button"
                      >
                        {creating ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Creating...
                          </>
                        ) : (
                          'Create Template'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog open={isPacketOpen} onOpenChange={(open) => {
                setIsPacketOpen(open);
                if (!open) {
                  setEditingPacket(null);
                  setNewPacket({
                    name: '',
                    description: '',
                    template_ids: [],
                    entity_type: 'candidate',
                    completion_message: '',
                    email_subject: '',
                    email_message: ''
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="create-packet-button">
                    <Plus className="w-4 h-4 mr-2" />
                    New Packet
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">
                      {editingPacket ? 'Edit Packet' : 'Create Packet'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePacket} className="space-y-4 mt-4" data-testid="create-packet-form">
                    {/* Packet Name */}
                    <div className="space-y-2">
                      <Label htmlFor="packet-name">Packet Name</Label>
                      <Input
                        id="packet-name"
                        value={newPacket.name}
                        onChange={(e) => setNewPacket({ ...newPacket, name: e.target.value })}
                        placeholder="e.g., New Hire Onboarding"
                        className="input-focus"
                        data-testid="packet-name-input"
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label htmlFor="packet-description">Description (optional)</Label>
                      <Textarea
                        id="packet-description"
                        value={newPacket.description}
                        onChange={(e) => setNewPacket({ ...newPacket, description: e.target.value })}
                        placeholder="Brief description of this packet"
                        className="input-focus"
                        rows={2}
                      />
                    </div>

                    {/* Entity Type */}
                    <div className="space-y-2">
                      <Label>Packet Type</Label>
                      <div className="grid grid-cols-3 gap-3">
                        <button
                          type="button"
                          onClick={() => setNewPacket({ ...newPacket, entity_type: 'candidate', template_ids: [] })}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            newPacket.entity_type === 'candidate'
                              ? 'border-[#4CAF50] bg-[#4CAF50]/10'
                              : 'border-border hover:border-[#4CAF50]/50'
                          }`}
                        >
                          <User className={`w-5 h-5 ${newPacket.entity_type === 'candidate' ? 'text-[#4CAF50]' : 'text-muted-foreground'}`} />
                          <span className="font-medium">Candidate</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPacket({ ...newPacket, entity_type: 'contact', template_ids: [] })}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            newPacket.entity_type === 'contact'
                              ? 'border-[#F9A825] bg-[#F9A825]/10'
                              : 'border-border hover:border-[#F9A825]/50'
                          }`}
                        >
                          <Building2 className={`w-5 h-5 ${newPacket.entity_type === 'contact' ? 'text-[#F9A825]' : 'text-muted-foreground'}`} />
                          <span className="font-medium">Contact</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewPacket({ ...newPacket, entity_type: 'placement', template_ids: [] })}
                          className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                            newPacket.entity_type === 'placement'
                              ? 'border-[#1a4a5e] bg-[#1a4a5e]/10'
                              : 'border-border hover:border-[#1a4a5e]/50'
                          }`}
                        >
                          <Briefcase className={`w-5 h-5 ${newPacket.entity_type === 'placement' ? 'text-[#1a4a5e]' : 'text-muted-foreground'}`} />
                          <span className="font-medium">Placement</span>
                        </button>
                      </div>
                    </div>

                    {/* Document Selection */}
                    <div className="space-y-2">
                      <Label>Select Documents ({newPacket.template_ids.length} selected)</Label>
                      <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
                        {filteredTemplates.length > 0 ? (
                          filteredTemplates.map(template => {
                            const isSelected = newPacket.template_ids.includes(template.id);
                            return (
                              <div 
                                key={template.id}
                                className={`flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors ${
                                  isSelected ? 'bg-primary/5' : ''
                                }`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleTemplateInPacket(template.id);
                                }}
                              >
                                <div 
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                    isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
                                  }`}
                                >
                                  {isSelected && (
                                    <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </div>
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{template.name}</p>
                                  <p className="text-xs text-muted-foreground">{template.fields?.length || 0} fields</p>
                                </div>
                                {/* Routing badge — only visible on placement
                                    packets, where templates of different entity
                                    types live side by side. */}
                                {newPacket.entity_type === 'placement' && (
                                  <span
                                    className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full border ${
                                      (template.entity_type || 'candidate') === 'contact'
                                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                                        : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                    }`}
                                  >
                                    {(template.entity_type || 'candidate') === 'contact' ? 'Contact' : 'Candidate'}
                                  </span>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-4 text-center text-muted-foreground text-sm">
                            No {newPacket.entity_type} templates available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email Settings */}
                    <div className="space-y-3 pt-2 border-t">
                      <Label className="text-sm font-medium">Email Notification</Label>
                      <div className="space-y-2">
                        <Input
                          value={newPacket.email_subject}
                          onChange={(e) => setNewPacket({ ...newPacket, email_subject: e.target.value })}
                          placeholder="Custom email subject (optional)"
                          className="input-focus text-sm"
                        />
                        <Textarea
                          value={newPacket.email_message}
                          onChange={(e) => setNewPacket({ ...newPacket, email_message: e.target.value })}
                          placeholder="Custom email message (optional) - The recipient will be notified that they have documents to complete"
                          className="input-focus text-sm"
                          rows={2}
                        />
                      </div>
                    </div>

                    {/* Completion Message */}
                    <div className="space-y-2">
                      <Label>Completion Message (optional)</Label>
                      <Textarea
                        value={newPacket.completion_message}
                        onChange={(e) => setNewPacket({ ...newPacket, completion_message: e.target.value })}
                        placeholder="Message shown to the recipient after completing all documents"
                        className="input-focus text-sm"
                        rows={2}
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsPacketOpen(false);
                          setEditingPacket(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={creating}
                        data-testid="submit-packet-button"
                      >
                        {creating ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                            Saving...
                          </>
                        ) : (
                          editingPacket ? 'Update Packet' : 'Create Packet'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Templates Tab Content */}
          <TabsContent value="templates" className="mt-6">
            {templates.length > 0 ? (
              (() => {
                // Group templates by entity_type and sort each group
                // alphabetically. Renders as three side-by-side columns
                // (Candidate · Placement · Contact) so recruiters can
                // scan the doc set by recipient type instantly. Empty
                // columns still render a header + "No documents" hint
                // so the layout doesn't collapse asymmetrically.
                const groups = {
                  candidate: [],
                  placement: [],
                  contact: [],
                };
                for (const t of templates) {
                  const k = (t.entity_type || 'candidate');
                  (groups[k] || groups.candidate).push(t);
                }
                for (const k of Object.keys(groups)) {
                  groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                }
                const columns = [
                  { key: 'candidate', label: 'Candidate', items: groups.candidate },
                  { key: 'placement', label: 'Placement', items: groups.placement },
                  { key: 'contact', label: 'Contact', items: groups.contact },
                ];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {columns.map((col) => {
                      const ec = ENTITY_COLORS[col.key];
                      return (
                        <div key={col.key} data-testid={`templates-col-${col.key}`}>
                          <div className="flex items-center justify-between mb-2 px-1">
                            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                              {col.label} Documents
                              <span className="text-xs text-muted-foreground font-normal">({col.items.length})</span>
                            </h3>
                          </div>
                          {col.items.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                              No {col.label.toLowerCase()} documents
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {col.items.map((template) => (
                                <div
                                  key={template.id}
                                  className="group neumorphic rounded-lg px-3 py-2.5 hover:shadow-md transition-shadow"
                                  data-testid={`template-card-${template.id}`}
                                >
                                  {/* Top row — title (truncate) + badge + menu, all on one line */}
                                  <div className="flex items-center gap-2">
                                    <div className={`w-7 h-7 rounded-md ${ec.lightBg} flex items-center justify-center flex-shrink-0`}>
                                      {col.key === 'contact' ? (
                                        <Building2 className={`w-3.5 h-3.5 ${ec.iconColor}`} />
                                      ) : (
                                        <User className={`w-3.5 h-3.5 ${ec.iconColor}`} />
                                      )}
                                    </div>
                                    <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={template.name}>
                                      {template.name}
                                    </h4>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                      {ec.label}
                                    </span>
                                    {template.is_informational && (
                                      <span
                                        className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex-shrink-0 whitespace-nowrap"
                                        title="Read-only · no required fields · skips Bullhorn write-back"
                                      >
                                        <Eye className="w-2.5 h-2.5" />
                                        Info
                                      </span>
                                    )}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                                          <MoreVertical className="w-3.5 h-3.5" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => navigate(getTenantPath(`/templates/${template.id}`))}>
                                          <Eye className="w-4 h-4 mr-2" />
                                          View & Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleDuplicate(template.id, template.name)}
                                          data-testid={`duplicate-template-${template.id}`}
                                        >
                                          <Copy className="w-4 h-4 mr-2" />
                                          Duplicate
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleDelete(template.id, template.name)}
                                          className="text-red-600"
                                        >
                                          <Trash2 className="w-4 h-4 mr-2" />
                                          Delete
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                  {/* Meta row — single line, no wrap */}
                                  <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground pl-9 whitespace-nowrap overflow-hidden">
                                    <span>{template.fields?.length || 0} fields</span>
                                    <span className="text-slate-300">·</span>
                                    <span>{format(new Date(template.created_at), 'MMM d, yyyy')}</span>
                                    {(template.candidate_instructions || template.description) && (
                                      <>
                                        <span className="text-slate-300">·</span>
                                        <span className="truncate" title={template.candidate_instructions || template.description}>{template.candidate_instructions || template.description}</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="neumorphic rounded-xl p-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-semibold text-lg mb-2">No templates yet</h3>
                <p className="text-muted-foreground font-body mb-6 max-w-md mx-auto">
                  Create your first template to start sending documents
                </p>
                <Button 
                  onClick={() => setIsCreateOpen(true)}
                  className="btn-primary"
                  data-testid="create-first-template"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Packets Tab Content */}
          <TabsContent value="packets" className="mt-6">
            {packets.length > 0 ? (
              (() => {
                const groups = { candidate: [], placement: [], contact: [] };
                for (const p of packets) {
                  const k = (p.entity_type || 'candidate');
                  (groups[k] || groups.candidate).push(p);
                }
                for (const k of Object.keys(groups)) {
                  groups[k].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                }
                const columns = [
                  { key: 'candidate', label: 'Candidate', items: groups.candidate },
                  { key: 'placement', label: 'Placement', items: groups.placement },
                  { key: 'contact', label: 'Contact', items: groups.contact },
                ];
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {columns.map((col) => {
                      const ec = ENTITY_COLORS[col.key];
                      return (
                        <div key={col.key} data-testid={`packets-col-${col.key}`}>
                          <div className="flex items-center justify-between mb-2 px-1">
                            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                              <span className={`inline-block w-2 h-2 rounded-full ${ec.bg}`} />
                              {col.label} Packets
                              <span className="text-xs text-muted-foreground font-normal">({col.items.length})</span>
                            </h3>
                          </div>
                          {col.items.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                              No {col.label.toLowerCase()} packets
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {col.items.map((packet) => {
                                const docCount = packet.template_ids?.length || 0;
                                return (
                                  <div
                                    key={packet.id}
                                    className="group neumorphic rounded-lg px-3 py-2.5 hover:shadow-md transition-shadow"
                                    data-testid={`packet-card-${packet.id}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                                        <Package className="w-3.5 h-3.5 text-primary" />
                                      </div>
                                      <h4 className="font-medium text-sm truncate flex-1 min-w-0" title={packet.name}>
                                        {packet.name}
                                      </h4>
                                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                        {ec.label}
                                      </span>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
                                            <MoreVertical className="w-3.5 h-3.5" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => handleEditPacket(packet)}>
                                            <Edit2 className="w-4 h-4 mr-2" />
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => handleDeletePacket(packet.id, packet.name)}
                                            className="text-red-600"
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground pl-9 whitespace-nowrap overflow-hidden">
                                      <Files className="w-3 h-3 flex-shrink-0" />
                                      <span>{docCount} doc{docCount !== 1 ? 's' : ''}</span>
                                      <span className="text-slate-300">·</span>
                                      <span>{format(new Date(packet.created_at), 'MMM d, yyyy')}</span>
                                      {packet.templates && packet.templates.length > 0 && (
                                        <>
                                          <span className="text-slate-300">·</span>
                                          <span className="truncate" title={packet.templates.map(t => t.name).join(', ')}>
                                            {packet.templates.slice(0, 3).map(t => t.name).join(', ')}
                                            {packet.templates.length > 3 && ` +${packet.templates.length - 3}`}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="neumorphic rounded-xl p-12 text-center">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-semibold text-lg mb-2">No packets yet</h3>
                <p className="text-muted-foreground font-body mb-6 max-w-md mx-auto">
                  Create a packet to group multiple documents together for sending
                </p>
                <Button 
                  onClick={() => setIsPacketOpen(true)}
                  className="btn-primary"
                  data-testid="create-first-packet"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Packet
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
